/**
 * Contextual Validity Panel (DC-723)
 *
 * Main panel for Phase 5 Contextual Validity assessment.
 * Displays problem declaration, proxy analysis, contextual fit score,
 * probing questions, and recommendations.
 *
 * Following PrivacyResults.tsx pattern.
 */

import React, { useEffect, useState } from 'react';
import { Target, ClipboardList, Search, Bot, Lightbulb } from 'lucide-react';
import { ProxyWarningCard } from './ProxyWarningCard';
import { ProbingQuestionCard, ProbingQuestion } from './ProbingQuestionCard';
import { ProblemDeclarationForm } from './ProblemDeclarationForm';
import { PerspectiveSelector, BusinessPerspective } from './PerspectiveSelector';
import { DictionaryPanel } from './DictionaryPanel';
import { RemediationScriptButton, DatabricksSourceInfo } from './RemediationScriptButton';
// DC-1518 — Domain Pack judgment surfaces (rules-above-narrative doctrine)
import { PackJudgmentSection } from './PackJudgmentSection';
import { DomainPackSelector } from './DomainPackSelector';
import type { PackJudgmentSummary, PackResolution } from './packJudgmentTypes';
import { useJourneyId } from '../../contexts/JourneyContext';
import './contextual-validity.css';
import '../../styles/domain-packs.css';

// ============================================================================
// TYPES
// ============================================================================

interface ProblemDeclaration {
  journey_id: string;
  problem_statement: string;
  problem_type: string;
  target_column?: string;
  domain: string;
  sensitivity_level: string;
  protected_attributes: string[];
  intended_use?: string;
  regulatory_context?: string;
  confirmed_by_user: boolean;
  business_perspective?: BusinessPerspective;
  kpis?: string[];
  source_systems?: string[];
  quality_standards?: string[];
}

// DC-1649 thin slice — mirrors server/contextualValidity/readinessVerdict.ts
interface ReadinessVerdict {
  decision:
    | 'ready_for_use'
    | 'develop_only'
    | 'not_ready'
    | 'use_mismatch'
    | 'fit_not_assessed'
    | 'no_declared_use'
    | 'no_readiness_assessment';
  headline: string;
  reasoning: string;
  capability: {
    score: number;
    grade: string | null;
    /** e.g. "AI Readiness" or "Asset Maturity (MECE v3.0)" — render verbatim. */
    source_label?: string;
  } | null;
  requirement: {
    fit_score: number | null;
    fit_level: string | null;
    overall_score: number;
    validation_status: string;
    problem_statement: string | null;
    intended_use: string | null;
    sensitivity_level: string | null;
  } | null;
  gaps: string[];
  methodology_version: string;
}

interface ProxyDetection {
  feature: string;
  proxies_for: string[];
  confidence: number;
  risk_level: 'low' | 'moderate' | 'high' | 'critical';
  detection_method: 'pattern' | 'correlation' | 'mutual_info';
  recommendation: string;
}

interface ProxyAnalysisResult {
  proxies: ProxyDetection[];
  proxy_risk_score: number;
  risk_level: string;
  high_risk_count: number;
  recommendations: string[];
  summary: string;
}

// ProbingQuestion type imported from ProbingQuestionCard

interface ContextualFitAssessment {
  fit_score: number;
  fit_level: 'excellent' | 'good' | 'adequate' | 'poor' | 'inappropriate';
  findings: Array<{
    category: string;
    finding: string;
    severity: string;
    recommendation: string;
  }>;
  ethical_considerations: string[];
  recommendations: Array<{
    priority: string;
    recommendation: string;
    category: string;
  }>;
  summary: string;
}

interface ContextualValidityResult {
  journey_id: string;
  analyzed_at: string;
  problem_declaration: ProblemDeclaration;
  proxy_analysis: ProxyAnalysisResult;
  contextual_fit?: ContextualFitAssessment;
  overall_score: number;
  validation_status: string;
  recommendations: Array<{
    priority: string;
    recommendation: string;
    category: string;
  }>;
  status: string;
}

interface ContextualFitPanelProps {
  journeyId?: string;
  onConfigureProblem?: () => void;
  onNavigateToBridge?: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const getFitLevelColor = (level: string): string => {
  switch (level) {
    case 'excellent': return 'fit-excellent';
    case 'good': return 'fit-good';
    case 'adequate': return 'fit-adequate';
    case 'poor': return 'fit-poor';
    case 'inappropriate': return 'fit-inappropriate';
    default: return '';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return 'score-good';
  if (score >= 60) return 'score-fair';
  if (score >= 40) return 'score-warning';
  return 'score-poor';
};

/**
 * Get priority icon as Unicode character (safe - no XSS risk)
 */
const getPriorityIcon = (priority: string): string => {
  switch (priority) {
    case 'immediate': return '\u{1F534}'; // Red circle
    case 'high': return '\u{1F7E0}'; // Orange circle
    case 'medium': return '\u{1F7E1}'; // Yellow circle
    case 'low': return '\u{1F7E2}'; // Green circle
    default: return '\u2022'; // Bullet
  }
};

/** Visible text companion to the colored priority circle (color is never the only channel). */
const getPriorityLabel = (priority: string): string => {
  switch (priority) {
    case 'immediate': return 'Immediate';
    case 'high': return 'High';
    case 'medium': return 'Medium';
    case 'low': return 'Low';
    default: return '';
  }
};

// ============================================================================
// Long-running run responses (DC-1630 keepalive posture)
// ============================================================================

/** The three keepalive-streamed CV runs share this envelope. */
interface AnalysisRunBody {
  success?: boolean;
  error?: string;
  pack_judgment?: PackJudgmentSummary | null;
}

const CUT_OFF_MESSAGE =
  'The analysis ran but the server response was cut off before it completed. ' +
  'Long-running analyses can be dropped by the network layer — re-run, and if it ' +
  'keeps happening tell your Compass consultant.';

/**
 * Read a JSON body without letting a non-JSON one masquerade as a code bug.
 *
 * A long analysis whose connection is dropped mid-flight returns an empty or
 * partial body, and a bare `response.json()` surfaces that as
 * "Unexpected end of JSON input" — a parser message shown to a user who did
 * nothing wrong and can act on nothing. Returns null instead so the caller can
 * say what actually happened.
 */
async function readJsonBody<T>(response: Response): Promise<T | null> {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ContextualFitPanel: React.FC<ContextualFitPanelProps> = ({
  journeyId: journeyIdProp,
  onConfigureProblem: _onConfigureProblem,
  onNavigateToBridge,
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ContextualValidityResult | null>(null);
  const [declaration, setDeclaration] = useState<ProblemDeclaration | null>(null);
  const [questions, setQuestions] = useState<ProbingQuestion[]>([]);
  const [showDeclarationForm, setShowDeclarationForm] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [dqStatus, setDqStatus] = useState<'loading' | 'available' | 'not_found' | 'not_completed'>('loading');
  const [perspective, setPerspective] = useState<BusinessPerspective | null>(null);
  const [profileColumns, setProfileColumns] = useState<Array<{ name: string; type: string }>>([]);
  const [databricksSource, setDatabricksSource] = useState<DatabricksSourceInfo | undefined>(undefined);
  // DC-1649 thin slice — read-side CV-conditional readiness verdict
  const [verdict, setVerdict] = useState<ReadinessVerdict | null>(null);
  // DC-1518 — the pack judgment from the last CV run this session. Rides the
  // run response only (judgments persist relationally, not on the CV result),
  // so older/legacy result rows render no judgment section — by design.
  const [packJudgment, setPackJudgment] = useState<PackJudgmentSummary | null>(null);
  // DC-1518 — display-only pack identity (which pack would govern a run).
  const [packResolution, setPackResolution] = useState<PackResolution | null>(null);

  // ============================================================================
  // LIFECYCLE: Load data
  // ============================================================================

  useEffect(() => {
    fetchData();
    checkAiStatus();
    checkDqStatus();
    fetchDatabricksSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  // DC-1518: display-only pack identity — which pack would govern a CV run on
  // this journey (domain binding vs perspective fallback). Additive; never
  // fails the panel.
  const effectivePerspective: BusinessPerspective =
    perspective || declaration?.business_perspective || 'rnd';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `/api/contextual-validity/journeys/${journeyId}/pack-resolution?perspective=${effectivePerspective}`
        );
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          setPackResolution(data.resolution ?? null);
        } else {
          setPackResolution(null);
        }
      } catch (err) {
        console.error('[ContextualFitPanel] Error fetching pack resolution:', err);
        if (!cancelled) setPackResolution(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [journeyId, effectivePerspective]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch declaration, results, and the conditional verdict in parallel
      const [declResponse, resultsResponse, verdictResponse] = await Promise.all([
        fetch(`/api/contextual-validity/declarations/${journeyId}`),
        fetch(`/api/contextual-validity/journeys/${journeyId}/results`),
        fetch(`/api/contextual-validity/journeys/${journeyId}/verdict`)
      ]);

      // DC-1649: the verdict is additive — never fail the panel over it.
      if (verdictResponse.ok) {
        const verdictData = await verdictResponse.json();
        setVerdict(verdictData.verdict ?? null);
      } else {
        setVerdict(null);
      }

      // Process declaration
      if (declResponse.ok) {
        const declData = await declResponse.json();
        setDeclaration(declData.declaration || null);
        if (declData.declaration?.business_perspective) {
          setPerspective(declData.declaration.business_perspective);
        }
      } else {
        setDeclaration(null);
      }

      // Process results
      if (resultsResponse.ok) {
        const resultsData = await resultsResponse.json();
        setResults(resultsData.result);
        // Results contain the declaration too
        if (resultsData.result?.problem_declaration) {
          setDeclaration(resultsData.result.problem_declaration);
        }
        // Also fetch questions
        await fetchQuestions();
      } else if (resultsResponse.status === 404) {
        // No results yet - that's okay if declaration exists
        setResults(null);
      } else {
        throw new Error(`Failed to fetch results: ${resultsResponse.statusText}`);
      }
    } catch (err: unknown) {
      console.error('[ContextualFitPanel] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const checkDqStatus = async () => {
    try {
      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/data-quality-columns`);
      if (response.ok) {
        setDqStatus('available');
        // Extract column names/types for dictionary panel
        const dqData = await response.json();
        if (dqData.column_profiles && Array.isArray(dqData.column_profiles)) {
          setProfileColumns(
            dqData.column_profiles.map((col: Record<string, unknown>) => ({
              name: col.name as string,
              type: (col.type || 'unknown') as string,
            }))
          );
        }
      } else if (response.status === 404) {
        setDqStatus('not_found');
      } else {
        const data = await response.json();
        if (data.status && data.status !== 'completed') {
          setDqStatus('not_completed');
        } else {
          setDqStatus('not_found');
        }
      }
    } catch (err) {
      console.error('[ContextualFitPanel] Error checking DQ status:', err);
      setDqStatus('not_found');
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/questions`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      }
    } catch (err) {
      console.error('[ContextualFitPanel] Error fetching questions:', err);
    }
  };

  const checkAiStatus = async () => {
    try {
      const response = await fetch('/api/contextual-validity/ai-status');
      if (response.ok) {
        const data = await response.json();
        setAiAvailable(data.available);
      }
    } catch (err) {
      console.error('[ContextualFitPanel] Error checking AI status:', err);
    }
  };

  const fetchDatabricksSource = async () => {
    try {
      const journeyResp = await fetch(`/api/journey/${journeyId}`);
      if (!journeyResp.ok) return;
      const { journey } = await journeyResp.json();
      const meta = journey?.metadata;
      if (meta?.data_source !== 'databricks' || !meta.databricks_connection_id || !meta.databricks_table) return;

      const tableStr = meta.databricks_table as string;
      const [catalog, schema, table] = tableStr.split('.');
      if (!catalog || !schema || !table) return;

      // Fetch connection details for name and workspace URL
      let connectionName = 'Databricks';
      let workspaceUrl = '';
      try {
        const connResp = await fetch(`/api/integrations/connections/${meta.databricks_connection_id}`);
        if (connResp.ok) {
          const connData = await connResp.json();
          connectionName = connData.name || connectionName;
          workspaceUrl = connData.endpointUrl || '';
        }
      } catch {
        // Use defaults — connection fetch is non-critical
      }

      setDatabricksSource({
        connectionId: meta.databricks_connection_id as string,
        connectionName,
        catalog,
        schema,
        table,
        workspaceUrl,
      });
    } catch (err) {
      console.error('[ContextualFitPanel] Error fetching Databricks source:', err);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleRunAnalysis = async () => {
    try {
      setGenerating('analysis');
      setError(null);

      // Use the new analyze-from-profile endpoint
      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/analyze-from-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await readJsonBody<{ error?: string }>(response);
        throw new Error(errorData?.error || `Analysis failed (HTTP ${response.status})`);
      }

      const data = await readJsonBody<AnalysisRunBody>(response);
      // A keepalive-streamed run that dies mid-flight leaves an unterminated
      // body: 200, headers long since sent, JSON that never closes.
      if (!data) {
        throw new Error(CUT_OFF_MESSAGE);
      }
      // DC-1630: long runs stream 200 + keepalive whitespace; post-header
      // failures arrive as 200 + { error }. Surface them.
      if (!data.success) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Analysis failed');
      }

      // DC-1518: the pack judgment rides the run response only.
      setPackJudgment(data.pack_judgment ?? null);

      await fetchData();
      await checkDqStatus();
    } catch (err: unknown) {
      console.error('[ContextualFitPanel] Analysis error:', err);
      const message = err instanceof Error ? err.message : 'An error occurred';
      // A domain-bound pack fails LOUD (mig-127 posture) — carry the server
      // message and route the user to the party who manages the binding.
      setError(
        packResolution?.bound
          ? `${message} Contact your Compass consultant — this domain's pack binding needs attention.`
          : message
      );
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerateQuestions = async () => {
    try {
      setGenerating('questions');

      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/generate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await readJsonBody<{ error?: string }>(response);
        throw new Error(errorData?.error || `Failed to generate questions (HTTP ${response.status})`);
      }

      const data = await readJsonBody<AnalysisRunBody>(response);
      if (!data) {
        throw new Error(CUT_OFF_MESSAGE);
      }
      // DC-1630: same error-on-200 keepalive guard as handleRunAnalysis.
      if (!data.success) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to generate questions');
      }

      await fetchQuestions();
    } catch (err: unknown) {
      console.error('[ContextualFitPanel] Generate questions error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerateFitAssessment = async () => {
    try {
      setGenerating('fit');

      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/generate-fit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await readJsonBody<{ error?: string }>(response);
        throw new Error(errorData?.error || `Failed to generate assessment (HTTP ${response.status})`);
      }

      const data = await readJsonBody<AnalysisRunBody>(response);
      if (!data) {
        throw new Error(CUT_OFF_MESSAGE);
      }
      // DC-1630: same error-on-200 keepalive guard as handleRunAnalysis.
      if (!data.success) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to generate assessment');
      }

      // DC-1518: generate-fit also runs the pack pipeline and carries the judgment.
      setPackJudgment(data.pack_judgment ?? null);

      await fetchData();
    } catch (err: unknown) {
      console.error('[ContextualFitPanel] Generate fit error:', err);
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(
        packResolution?.bound
          ? `${message} Contact your Compass consultant — this domain's pack binding needs attention.`
          : message
      );
    } finally {
      setGenerating(null);
    }
  };

  const handleQuestionAnswered = async (questionId: string, answer: string) => {
    // Update local state
    setQuestions(prev =>
      prev.map(q =>
        q.id === questionId ? { ...q, answered: true, answer } : q
      )
    );
  };

  const handleDeclarationSaved = async () => {
    setShowDeclarationForm(false);
    // Only fetch the declaration — do NOT fetch old results.
    // This ensures the user lands on the "Ready to Analyze" state
    // where they must explicitly click "Run Analysis" to trigger
    // a fresh API call with the updated declaration.
    try {
      const declResponse = await fetch(`/api/contextual-validity/declarations/${journeyId}`);
      if (declResponse.ok) {
        const declData = await declResponse.json();
        setDeclaration(declData.declaration || null);
        if (declData.declaration?.business_perspective) {
          setPerspective(declData.declaration.business_perspective);
        }
      }
    } catch (err) {
      console.error('[ContextualFitPanel] Error fetching declaration after save:', err);
    }
    // Re-check DQ status so the dictionary panel and run button are enabled
    await checkDqStatus();
    // Ensure old results stay cleared so user sees "Ready to Analyze"
    setResults(null);
    setQuestions([]);
  };

  // ============================================================================
  // RENDER: Loading state
  // ============================================================================

  if (loading) {
    return (
      <div className="contextual-fit-panel loading">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading contextual validity results...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Error state
  // ============================================================================

  if (error && !results) {
    return (
      <div className="contextual-fit-panel error">
        <div className="error-state">
          <span className="error-icon">&#9888;</span>
          <h3>Failed to Load Results</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Setup state (no results yet)
  // ============================================================================

  if (!results) {
    // Show declaration form if requested
    if (showDeclarationForm) {
      return (
        <div className="contextual-fit-panel">
          <ProblemDeclarationForm
            journeyId={journeyId}
            onSave={handleDeclarationSaved}
            onCancel={() => setShowDeclarationForm(false)}
            existingDeclaration={declaration}
            perspective={perspective || declaration?.business_perspective || 'rnd'}
          />
        </div>
      );
    }

    // Declaration exists - show "Ready to Analyze" state
    if (declaration) {
      return (
        <div className="contextual-fit-panel">
          {error && (
            <div className="panel-error">
              <span className="error-icon">&#9888;</span>
              {error}
              <button className="btn-link" onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          <div className="ready-to-analyze-state">
            <div className="setup-icon">&#9989;</div>
            <h2>Problem Declaration Complete</h2>
            <p>Your ML problem has been defined. Now run the analysis to check for proxy discrimination risks.</p>

            {/* Declaration Summary */}
            <div className="declaration-summary-card">
              <div className="declaration-header">
                <h4><ClipboardList size={16} aria-hidden="true" className="cv-h-icon" /> Problem Declaration</h4>
                <button className="btn-link" onClick={() => setShowDeclarationForm(true)}>
                  Edit
                </button>
              </div>
              <div className="declaration-details">
                <div className="detail-row">
                  <span className="detail-label">Problem:</span>
                  <span className="detail-value">{declaration.problem_statement}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{declaration.problem_type.replace(/_/g, ' ')}</span>
                </div>
                {declaration.target_column && (
                  <div className="detail-row">
                    <span className="detail-label">Target:</span>
                    <span className="detail-value">{declaration.target_column}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Domain:</span>
                  <span className="detail-value">{declaration.domain}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Sensitivity:</span>
                  <span className={`sensitivity-badge sensitivity-${declaration.sensitivity_level}`}>
                    {declaration.sensitivity_level.toUpperCase()}
                  </span>
                </div>
                {declaration.protected_attributes.length > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Protected Attributes:</span>
                    <span className="detail-value">
                      {declaration.protected_attributes.map((attr, idx) => (
                        <span key={idx} className="protected-tag">{attr}</span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* DC-1518: pack identity on the launch pad — bound shows the
                static identity row; unbound shows one muted provenance line. */}
            <DomainPackSelector resolution={packResolution} />

            {/* Data Quality Status */}
            <div className="dq-status-card">
              {dqStatus === 'loading' && (
                <p className="dq-status checking">
                  <span className="spinner-small"></span>
                  Checking Data Quality status...
                </p>
              )}
              {dqStatus === 'available' && (
                <p className="dq-status available">
                  &#10003; Data Quality analysis available - ready to run contextual validity
                </p>
              )}
              {dqStatus === 'not_found' && (
                <p className="dq-status warning">
                  &#9888; Data Quality analysis not found. Please run the Data Quality station first.
                </p>
              )}
              {dqStatus === 'not_completed' && (
                <p className="dq-status warning">
                  &#9888; Data Quality analysis is still in progress. Please wait for it to complete.
                </p>
              )}
            </div>

            {/* Data Dictionary Panel (pre-analysis) — expanded so user sees it */}
            {dqStatus === 'available' && profileColumns.length > 0 && (
              <DictionaryPanel
                journeyId={journeyId}
                profileColumns={profileColumns}
                initiallyExpanded={true}
              />
            )}

            {/* Run Analysis Button */}
            <button
              className="btn btn-primary btn-large"
              onClick={handleRunAnalysis}
              disabled={generating === 'analysis' || dqStatus !== 'available'}
            >
              {generating === 'analysis' ? (
                <>
                  <span className="spinner-small"></span>
                  Analyzing...
                </>
              ) : (
                <><Search size={16} aria-hidden="true" className="cv-h-icon" /> Run Contextual Validity Analysis</>
              )}
            </button>

            {dqStatus !== 'available' && dqStatus !== 'loading' && (
              <p className="help-text">
                The analysis uses column profiles from Data Quality. Please complete that station first.
              </p>
            )}
          </div>
        </div>
      );
    }

    // No declaration yet - show perspective selector then initial setup
    return (
      <div className="contextual-fit-panel">
        <div className="setup-state">
          <div className="setup-icon"><Target size={36} aria-hidden="true" /></div>
          <h2>Contextual Validity Assessment</h2>
          <p>
            Evaluate whether your data is appropriate for your intended use.
            Select a perspective to customize the analysis for your domain.
          </p>

          {/* DC-1518: when a domain binding governs, the pack identity row
              replaces the perspective control (hidden, not disabled — the
              binding is consultant-managed, spec §4). */}
          {packResolution?.bound && (
            <DomainPackSelector resolution={packResolution} />
          )}

          {/* Step 0: Select Perspective */}
          {!packResolution?.bound && !perspective && (
            <PerspectiveSelector
              selected={perspective}
              onSelect={(p) => setPerspective(p)}
            />
          )}

          {/* Step flow after perspective selected (or a binding governs) */}
          {(perspective || packResolution?.bound) && (
            <>
              {!packResolution?.bound && perspective && (
                <DomainPackSelector resolution={packResolution}>
                  <div className="perspective-selected-badge">
                    {perspective === 'rnd' ? 'R&D / Research' :
                     perspective === 'quality' ? 'Quality / GxP' :
                     'Commercial Analytics'}
                    <button className="btn-link" onClick={() => setPerspective(null)}>Change</button>
                  </div>
                </DomainPackSelector>
              )}

              <div className="setup-steps">
                <div className="setup-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>
                      {perspective === 'commercial' ? 'Define Objective' :
                       perspective === 'quality' ? 'Define Assessment' :
                       'Declare Problem'}
                    </h4>
                    <p>
                      {perspective === 'commercial' ? 'Define the commercial analytics objective' :
                       perspective === 'quality' ? 'Define the quality assessment scope' :
                       'Define the ML problem you\'re solving'}
                    </p>
                  </div>
                </div>
                <div className="step-arrow">&#8594;</div>
                <div className="setup-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Run Analysis</h4>
                    <p>
                      {perspective === 'commercial' ? 'Assess source consistency and attribution' :
                       perspective === 'quality' ? 'Assess ALCOA+ integrity and compliance' :
                       'Detect proxy features and assess fit'}
                    </p>
                  </div>
                </div>
                <div className="step-arrow">&#8594;</div>
                <div className="setup-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Review Findings</h4>
                    <p>Answer questions and review recommendations</p>
                  </div>
                </div>
              </div>

              <button
                className="btn btn-primary btn-large"
                onClick={() => setShowDeclarationForm(true)}
              >
                {perspective === 'commercial' ? 'Start: Define Commercial Objective' :
                 perspective === 'quality' ? 'Start: Define Quality Assessment' :
                 'Start: Declare ML Problem'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Results
  // ============================================================================

  const { problem_declaration, proxy_analysis, contextual_fit, overall_score, recommendations } = results;
  const answeredCount = questions.filter(q => q.answered).length;
  const unansweredQuestions = questions.filter(q => !q.answered);

  return (
    <div className="contextual-fit-panel">
      {error && (
        <div className="panel-error">
          <span className="error-icon">&#9888;</span>
          {error}
          <button className="btn-link" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Header with Overall Score */}
      <div className="panel-header">
        <div className="header-left">
          <h2>
            <span className="header-icon"><Target size={22} aria-hidden="true" /></span>
            Contextual Validity Assessment
          </h2>
          <p className="panel-subtitle">
            Data-problem fit and proxy discrimination analysis
          </p>
        </div>
        <div className="header-score">
          <div className={`score-circle ${getScoreColor(overall_score)}`}>
            <span className="score-value">{Math.round(overall_score)}</span>
            <span className="score-label">/ 100</span>
          </div>
          {contextual_fit && (
            <span className={`fit-badge ${getFitLevelColor(contextual_fit.fit_level)}`}>
              {contextual_fit.fit_level.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* DC-1649 thin slice — CV-conditional readiness verdict.
          "'AI-ready' is a relationship, not a score": capability (intrinsic
          AI Readiness) and requirement (this CV result) stay separate named
          inputs; the verdict reads them together per the declared use.

          Presentation rule (operator, 2026-06-11): when only ONE leg has been
          measured, the measured leg carries the visual weight and the absent
          leg goes quiet — never the other way around. The center pill renders
          the decision only when there IS a decision; for the absence
          narrations it would just repeat the quiet panel's text at maximum
          volume, so it collapses to a slim connector and the reasoning line
          below carries the story. */}
      {verdict && (() => {
        const capabilityMissing = !verdict.capability;
        const requirementMissing = !verdict.requirement?.fit_level;
        const absenceNarration = ['no_declared_use', 'no_readiness_assessment', 'fit_not_assessed']
          .includes(verdict.decision);
        const capabilityCls = [
          'cv-verdict__panel cv-verdict__panel--capability',
          capabilityMissing ? 'cv-verdict__panel--empty' : '',
          !capabilityMissing && requirementMissing ? 'cv-verdict__panel--lead' : '',
        ].join(' ').trim();
        const requirementCls = [
          'cv-verdict__panel cv-verdict__panel--requirement',
          requirementMissing ? 'cv-verdict__panel--empty' : '',
          !requirementMissing && capabilityMissing ? 'cv-verdict__panel--lead' : '',
        ].join(' ').trim();
        return (
        <div className="cv-verdict" data-testid="cv-verdict">
          <div className="cv-verdict__eyebrow">
            &lsquo;AI-ready&rsquo; is a relationship, not a score
          </div>
          <div className="cv-verdict__grid">
            <div className={capabilityCls}>
              <span className="cv-verdict__panel-label">Capability</span>
              <span className="cv-verdict__panel-value">
                {verdict.capability ? Math.round(verdict.capability.score) : 'Not yet measured'}
              </span>
              <span className="cv-verdict__panel-sub">
                {verdict.capability
                  ? `${verdict.capability.source_label ?? 'AI Readiness'}${verdict.capability.grade ? ` · grade ${verdict.capability.grade}` : ''}`
                  : 'Run the analysis stations to score this leg'}
              </span>
            </div>
            {absenceNarration ? (
              <div className="cv-verdict__connector" aria-hidden="true">vs</div>
            ) : (
              <div className={`cv-verdict__decision cv-verdict__decision--${verdict.decision}`}>
                <span className="cv-verdict__decision-label">{verdict.headline}</span>
              </div>
            )}
            <div
              className={requirementCls}
              title={verdict.requirement?.intended_use || undefined}
            >
              <span className="cv-verdict__panel-label">Requirement</span>
              <span className="cv-verdict__panel-value">
                {verdict.requirement?.fit_level ? verdict.requirement.fit_level : 'Not yet assessed'}
              </span>
              <span className="cv-verdict__panel-sub cv-verdict__panel-sub--clamped">
                {/* The one-sentence problem statement reads like a designed
                    summary; the full intended-use declaration (often an
                    essay) lives in the tooltip and verbatim in the Problem
                    Declaration section below. */}
                {verdict.requirement?.problem_statement
                  ? `for: ${verdict.requirement.problem_statement}`
                  : verdict.requirement?.intended_use
                    ? `for: ${verdict.requirement.intended_use}`
                    : 'Contextual fit for the declared use'}
              </span>
            </div>
          </div>
          <p className="cv-verdict__reasoning">{verdict.reasoning}</p>
          {verdict.gaps.length > 0 && (
            <ul className="cv-verdict__gaps">
              {verdict.gaps.map((gap) => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
          )}
        </div>
        );
      })()}

      {/* Problem Declaration Summary */}
      <div className="section problem-declaration-section">
        <div className="section-header">
          <h3><ClipboardList size={17} aria-hidden="true" className="cv-h-icon" /> Problem Declaration</h3>
          <button className="btn-link" onClick={() => setShowDeclarationForm(true)}>
            Edit
          </button>
        </div>
        <div className="declaration-summary">
          <div className="declaration-statement">
            <strong>Problem:</strong> {problem_declaration.problem_statement}
          </div>
          <div className="declaration-meta">
            <span className="meta-item">
              <strong>Type:</strong> {problem_declaration.problem_type.replace(/_/g, ' ')}
            </span>
            {problem_declaration.target_column && (
              <span className="meta-item">
                <strong>Target:</strong> {problem_declaration.target_column}
              </span>
            )}
            <span className="meta-item">
              <strong>Domain:</strong> {problem_declaration.domain}
            </span>
            <span className={`sensitivity-badge sensitivity-${problem_declaration.sensitivity_level}`}>
              {problem_declaration.sensitivity_level.toUpperCase()} SENSITIVITY
            </span>
          </div>
          {problem_declaration.protected_attributes.length > 0 && (
            <div className="protected-attributes-summary">
              <strong>Protected Attributes:</strong>
              {problem_declaration.protected_attributes.map((attr, idx) => (
                <span key={idx} className="protected-tag">{attr}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Declaration Form Modal */}
      {showDeclarationForm && (
        <div className="modal-overlay" onClick={() => setShowDeclarationForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <ProblemDeclarationForm
              journeyId={journeyId}
              onSave={handleDeclarationSaved}
              onCancel={() => setShowDeclarationForm(false)}
              existingDeclaration={problem_declaration}
              mode="modal"
              perspective={perspective || problem_declaration.business_perspective || 'rnd'}
            />
          </div>
        </div>
      )}

      {/* Data Dictionary Panel - collapsible, above analysis sections */}
      <DictionaryPanel
        journeyId={journeyId}
        profileColumns={profileColumns}
      />

      {/* Proxy Analysis Section - only show if protected attributes were declared */}
      {problem_declaration.protected_attributes && problem_declaration.protected_attributes.length > 0 ? (
        <div className="section proxy-analysis-section">
          <div className="section-header">
            <h3><Search size={17} aria-hidden="true" className="cv-h-icon" /> Proxy Analysis</h3>
            <span className={`risk-badge risk-${proxy_analysis.risk_level}`}>
              {proxy_analysis.high_risk_count} High Risk
            </span>
          </div>

          {proxy_analysis.proxies.length > 0 ? (
            <div className="proxy-list">
              {proxy_analysis.proxies.map((proxy, idx) => (
                <ProxyWarningCard key={idx} proxy={proxy} />
              ))}
            </div>
          ) : (
            <div className="no-proxies">
              <span className="success-icon">&#10003;</span>
              <p>No proxy discrimination risks detected in your features.</p>
            </div>
          )}

          {proxy_analysis.summary && (
            <div className="analysis-summary">
              <p>{proxy_analysis.summary}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="section proxy-analysis-section collapsed">
          <div className="section-header">
            <h3><Search size={17} aria-hidden="true" className="cv-h-icon" /> Proxy Analysis</h3>
            <span className="badge-muted">N/A</span>
          </div>
          <p className="section-note">
            No protected attributes declared. Proxy discrimination analysis is not applicable for this dataset.
            If you want to check for proxy variables, edit the problem declaration and select protected attributes.
          </p>
        </div>
      )}

      {/* DC-1518: Domain Pack judgment — deterministic, cited findings render
          ABOVE the AI fit narrative below (rules-above-narrative doctrine:
          findings outrank opinion on an audit surface). Renders nothing when
          the run carried no pack judgment (legacy/older results). */}
      <PackJudgmentSection summary={packJudgment} />

      {/* Contextual Validity Section */}
      <div className="section contextual-fit-section">
        <div className="section-header">
          <h3>Contextual Validity Assessment</h3>
          {aiAvailable && (
            <button
              className="btn-secondary btn-small"
              onClick={handleGenerateFitAssessment}
              disabled={generating === 'fit'}
            >
              {generating === 'fit' ? (
                <>
                  <span className="spinner-small"></span>
                  Generating...
                </>
              ) : contextual_fit ? (
                'Regenerate'
              ) : (
                <><Bot size={15} aria-hidden="true" className="cv-h-icon" /> Generate with AI</>
              )}
            </button>
          )}
        </div>

        {contextual_fit ? (
          <div className="fit-assessment">
            <div className="fit-score-display">
              <div className={`fit-score ${getFitLevelColor(contextual_fit.fit_level)}`}>
                <span className="score-value">{Math.round(contextual_fit.fit_score)}</span>
                <span className="score-label">Fit Score</span>
              </div>
              <div className={`fit-level-badge ${getFitLevelColor(contextual_fit.fit_level)}`}>
                {contextual_fit.fit_level.toUpperCase()}
              </div>
            </div>

            <div className="fit-summary">
              <p>{contextual_fit.summary}</p>
            </div>

            {/* Findings */}
            {contextual_fit.findings.length > 0 && (
              <div className="findings-list">
                <h4>Key Findings</h4>
                {contextual_fit.findings.map((finding, idx) => (
                  <div key={idx} className={`finding-card severity-${finding.severity}`}>
                    <div className="finding-header">
                      <span className="finding-category">{finding.category}</span>
                      <span className={`severity-badge severity-${finding.severity}`}>
                        {finding.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="finding-text">{finding.finding}</p>
                    {finding.recommendation && (
                      <p className="finding-recommendation">
                        <strong>Recommendation:</strong> {finding.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Ethical Considerations */}
            {contextual_fit.ethical_considerations.length > 0 && (
              <div className="ethical-considerations">
                <h4>&#9878; Ethical Considerations</h4>
                <ul>
                  {contextual_fit.ethical_considerations.map((consideration, idx) => (
                    <li key={idx}>{consideration}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="no-fit-assessment">
            <p>
              No AI-generated fit assessment yet.
              {aiAvailable
                ? ' Click "Generate with AI" to get a detailed assessment.'
                : ' AI is not available - configure API key in settings.'}
            </p>
          </div>
        )}
      </div>

      {/* Probing Questions Section */}
      <div className="section probing-questions-section">
        <div className="section-header">
          <h3>&#10067; Probing Questions</h3>
          <div className="questions-meta">
            <span className="answered-count">
              {answeredCount}/{questions.length} answered
            </span>
            {aiAvailable && (
              <button
                className="btn-secondary btn-small"
                onClick={handleGenerateQuestions}
                disabled={generating === 'questions'}
              >
                {generating === 'questions' ? (
                  <>
                    <span className="spinner-small"></span>
                    Generating...
                  </>
                ) : (
                  <><Bot size={15} aria-hidden="true" className="cv-h-icon" /> Generate More</>
                )}
              </button>
            )}
          </div>
        </div>

        {questions.length > 0 ? (
          <div className="questions-list">
            {/* Show unanswered first */}
            {unansweredQuestions.map(question => (
              <ProbingQuestionCard
                key={question.id}
                question={question}
                journeyId={journeyId}
                onAnswer={handleQuestionAnswered}
              />
            ))}
            {/* Then answered */}
            {questions.filter(q => q.answered).map(question => (
              <ProbingQuestionCard
                key={question.id}
                question={question}
                journeyId={journeyId}
                onAnswer={handleQuestionAnswered}
              />
            ))}
          </div>
        ) : (
          <div className="no-questions">
            <p>
              No probing questions yet.
              {aiAvailable
                ? ' Generate AI-powered questions to better understand your data context.'
                : ' AI is not available for question generation.'}
            </p>
          </div>
        )}
      </div>

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <div className="section recommendations-section">
          <h3><Lightbulb size={17} aria-hidden="true" className="cv-h-icon" /> Recommendations</h3>
          <div className="recommendations-list">
            {recommendations.map((rec, idx) => (
              <div key={idx} className={`recommendation-item priority-${(rec.priority || 'medium').toLowerCase()}`}>
                <span
                  className="priority-icon"
                  role="img"
                  aria-label={`${getPriorityLabel((rec.priority || 'medium').toLowerCase())} priority`}
                >
                  {getPriorityIcon((rec.priority || 'medium').toLowerCase())}
                </span>
                <div className="recommendation-content">
                  <span className="recommendation-category">
                    {rec.category} · {getPriorityLabel((rec.priority || 'medium').toLowerCase())} priority
                  </span>
                  <p>{rec.recommendation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remediation Script CTA */}
      {results && recommendations.length > 0 && (
        <div className="remediation-cta-section">
          <div className="remediation-cta-content">
            <div className="remediation-cta-text">
              <h3>&#9889; Generate Remediation Script</h3>
              <p>
                Turn these {recommendations.length} recommendation{recommendations.length !== 1 ? 's' : ''} into
                a runnable Python script. Review each fix section-by-section with confidence ratings before downloading.
              </p>
            </div>
            <RemediationScriptButton
              journeyId={journeyId}
              disabled={!results}
              databricksSource={databricksSource}
            />
          </div>
        </div>
      )}

      {/* Bridge AI Upgrade CTA (DC-1471) */}
      {results && onNavigateToBridge && (
        <div className="bridge-upgrade-cta">
          <div className="bridge-upgrade-content">
            <div className="bridge-upgrade-icon">&#9889;</div>
            <div className="bridge-upgrade-text">
              <h3>Get Enhanced Recommendations</h3>
              <p>
                Strategic Intelligence can enhance these findings with ROI estimates,
                Pistoia Alliance value driver mapping, and governance action steps.
              </p>
            </div>
          </div>
          <button
            className="btn btn-primary bridge-upgrade-btn"
            onClick={onNavigateToBridge}
          >
            View Enhanced Actions in Strategic Intelligence &rarr;
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="panel-actions">
        <button
          className="btn btn-primary"
          onClick={() => {
            if (!confirm('Re-run the analysis? This clears the current results, problem declaration, and answered questions — you will start from the problem-definition step.')) {
              return;
            }
            setResults(null);
            setPerspective(null);
            setDeclaration(null);
            setQuestions([]);
            setPackJudgment(null);
          }}
          disabled={!!generating}
        >
          Re-run Analysis
        </button>
      </div>

      {/* Metadata */}
      <div className="panel-metadata">
        <span>Last analyzed: {new Date(results.analyzed_at).toLocaleString()}</span>
        <span>Status: {results.status}</span>
      </div>
    </div>
  );
};

export default ContextualFitPanel;
